import { useQuery } from '@tanstack/react-query';
import { getMonopolyReport, type MonopolyPayload } from './report';
import { MONOPOLY_EMPTY, MONOPOLY_PREVIEW } from './fixture';

/*
 * "Realtime" in this portal means POLLING the frozen payload the pipeline
 * writes — the pipeline's cadence is the freshness floor, and the "updated"
 * stamp in the header is the payload's own `last_refreshed_at`, never a
 * client-side clock. The design's live ticker (a number incrementing every
 * 1.2s) is a design flourish; showing invented movement on a client dashboard
 * would be dishonest, so we don't.
 *
 * Until the pipeline exists every call 404s, so we fall back:
 *   - dev  → the design's sample figures, so the UI can be reviewed as the real thing
 *   - prod → zeros, so a deployed build never shows a client invented figures
 * Either way `isPreview` is true and the header says so.
 */
export const MONOPOLY_POLL_MS = 60_000;

export function useMonopolyReport(
  reportId: string,
  baseUrl: string,
): { data: MonopolyPayload; isPreview: boolean; lastRefreshedAt: string | null; isFetching: boolean } {
  const query = useQuery({
    queryKey: ['monopoly-report', baseUrl, reportId],
    queryFn: () => getMonopolyReport(reportId, baseUrl),
    retry: false,
    // Keep the live view live without hammering the API while the tab is hidden.
    refetchInterval: MONOPOLY_POLL_MS,
    refetchIntervalInBackground: false,
  });

  if (query.data) {
    return { data: query.data.data, isPreview: false, lastRefreshedAt: query.data.meta?.last_refreshed_at ?? null, isFetching: query.isFetching };
  }
  return { data: import.meta.env.DEV ? MONOPOLY_PREVIEW : MONOPOLY_EMPTY, isPreview: true, lastRefreshedAt: null, isFetching: query.isFetching };
}
